/* eslint-disable @nx/enforce-module-boundaries */
import { API_IDS } from '@intigral/constant';
import { DI } from '@intigral/constant/config/DIContainer';
import { container } from '@intigral/domain/lib/DataDomain';
import { iStreamHarnessService } from '@intigral/domain/lib/application/services/StreamHarnessService';
import {
    IAcquireLockRequestBody,
    IAcquireLockRequestHeaders,
    IAcquireLockRequestParams,
    IAcquireLockResponse,
    IDeleteLockRequestParams,
} from '@intigral/domain/lib/entity/interface/iStreamHarness';
import { IApiPathManager } from '@intigral/domain/lib/infrastructures/config/ApiPathManager';
import h from '../helpers/common';
import user from '../helpers/user';
import cdnHandler, { retryN } from '../helpers/CDNHandler';
import { useConfigurationSections } from './useConfiguration';

export const useConcurrencyService = () => {
    /**
     * Function to get unique device signature which will distinguish devices.
     * @returns {string}
     */
    const getDeviceSignature = () => {
        const storedHash = window.localStorage.getItem('concurHash');
        if (!storedHash) {
            const timeHash = Date.now().toString(36);
            window.localStorage.setItem('concurHash', timeHash);
            return timeHash;
        }
        return storedHash;
    };

    const acquireLock = async (QueryParams) => {
        const queryParams: IAcquireLockRequestParams = {
            apiKey: user.getAPIKey(),
            publicUrl: QueryParams.publicUrl,
            switch: QueryParams.switch,
            type: QueryParams.type,
        };
        const requestBody: IAcquireLockRequestBody = {
            accountId: user.getAccountId(),
            deviceGuid: h.getDeviceGuid() + '~' + getDeviceSignature(),
        };
        const headers: IAcquireLockRequestHeaders = {
            authToken: user.getUserToken(),
            identityToken: user.getIdentityToken(),
        };
        const response = await container
            .resolve<iStreamHarnessService>(DI.SERVICES.IStreamHarnessService)
            .acquireLock(queryParams, requestBody, headers);
        return response;
    };

    const refreshLock = async (QueryParams, lockId) => {
        const queryParams: IAcquireLockRequestParams = {
            apiKey: user.getAPIKey(),
            ...(QueryParams && QueryParams.publicUrl && { publicUrl: QueryParams.publicUrl }),
            ...(QueryParams && QueryParams.switch && { switch: QueryParams.switch }),
            ...(QueryParams && QueryParams.type && { type: QueryParams.type }),
        };
        const requestBody: IAcquireLockRequestBody = {
            accountId: user.getAccountId(),
            deviceGuid: h.getDeviceGuid() + '~' + getDeviceSignature(),
            lockId,
        };
        const headers: IAcquireLockRequestHeaders = {
            authToken: user.getUserToken(),
            identityToken: user.getIdentityToken(),
        };
        const response = await container
            .resolve<iStreamHarnessService>(DI.SERVICES.IStreamHarnessService)
            .refreshLock(queryParams, requestBody, headers);
        return response;
    };

    const unLock = async (lockId) => {
        const queryParams: IDeleteLockRequestParams = {
            apiKey: user.getAPIKey(),
            accountId: user.getAccountId(),
            lockId,
        };
        const headers: IAcquireLockRequestHeaders = {
            authToken: user.getUserToken(),
            identityToken: user.getIdentityToken(),
        };
        const response = await container
            .resolve<iStreamHarnessService>(DI.SERVICES.IStreamHarnessService)
            .deleteLock(queryParams, headers);
        return response;
    };

    return {
        acquireLock,
        refreshLock,
        unLock,
    };
};

export const useConcurrencyServiceWithFetch = () => {
    const { video = {} } = useConfigurationSections();
    const concurrencyConfig = (video as any)?.streamConcurrencyV2 || {};
    const { allowFailureRetry, failureMaxRetries, failureMultiplierMills } = concurrencyConfig;
    /**
     * Function to get unique device signature which will distinguish devices.
     * @returns {string}
     */
    const getDeviceSignature = () => {
        const storedHash = window.localStorage.getItem('concurHash');
        if (!storedHash) {
            const timeHash = Date.now().toString(36);
            window.localStorage.setItem('concurHash', timeHash);
            return timeHash;
        }
        return storedHash;
    };

    const apiPathManager = container.resolve<IApiPathManager>(DI.UTILS.IApiPathManager);

    const acquireLock = async (QueryParams) => {
        const queryParams: IAcquireLockRequestParams = {
            apiKey: user.getAPIKey(),
            publicUrl: QueryParams.publicUrl,
            switch: QueryParams.switch,
            type: QueryParams.type,
        };
        const requestBody: IAcquireLockRequestBody = {
            accountId: user.getAccountId(),
            deviceGuid: h.getDeviceGuid() + '~' + getDeviceSignature(),
        };

        const endpoint = apiPathManager.getPath(API_IDS.STREAM_HARNESS_ACQUIRE_LOCK);
        const url = new URL(endpoint.replace('{apiKey}', queryParams.apiKey));

        const publicUrl = new URL(QueryParams.publicUrl);
        const searchParams = publicUrl.searchParams;
        const publicUrlParameter = `${publicUrl.origin}${publicUrl.pathname}`;
        url.searchParams.set('publicUrl', publicUrlParameter);

        searchParams.forEach((value, key) => {
            url.searchParams.set(key, value);
        });

        url.searchParams.set('switch', queryParams.switch);
        url.searchParams.set('type', queryParams.type);

        let response;

        if (allowFailureRetry) {
            // Retry logic enabled
            response = await retryN(
                () =>
                    fetch(url, {
                        method: 'POST',
                        body: JSON.stringify(requestBody),
                        headers: {
                            'Content-Type': 'application/json',
                            Authorization: user.getUserToken(),
                            identityToken: user.getIdentityToken(),
                        },
                    }),
                failureMaxRetries || 3,
                failureMultiplierMills || 500,
                null
            );
        } else {
            // No retry — just call fetch once
            response = await fetch(url, {
                method: 'POST',
                body: JSON.stringify(requestBody),
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: user.getUserToken(),
                    identityToken: user.getIdentityToken(),
                },
            });
        }
        const parsedResponse: IAcquireLockResponse = await response.json();
        return { ...parsedResponse, status: response?.status };
    };

    const refreshLock = async (QueryParams, lockId) => {
        const queryParams: IAcquireLockRequestParams = {
            apiKey: user.getAPIKey(),
            ...(QueryParams && QueryParams.publicUrl && { publicUrl: QueryParams.publicUrl }),
            ...(QueryParams && QueryParams.switch && { switch: QueryParams.switch }),
            ...(QueryParams && QueryParams.type && { type: QueryParams.type }),
        };
        const requestBody: IAcquireLockRequestBody = {
            accountId: user.getAccountId(),
            deviceGuid: h.getDeviceGuid() + '~' + getDeviceSignature(),
            lockId,
        };
        const headers: IAcquireLockRequestHeaders = {
            authToken: user.getUserToken(),
            identityToken: user.getIdentityToken(),
        };

        const endpoint = apiPathManager.getPath(API_IDS.STREAM_HARNESS_ACQUIRE_LOCK);
        const url = new URL(endpoint.replace('{apiKey}', queryParams.apiKey));

        if (QueryParams && QueryParams.publicUrl) {
            const publicUrl = new URL(QueryParams.publicUrl);
            const searchParams = publicUrl.searchParams;
            const publicUrlParameter = `${publicUrl.origin}${publicUrl.pathname}`;
            url.searchParams.set('publicUrl', publicUrlParameter);

            searchParams.forEach((value, key) => {
                url.searchParams.set(key, value);
            });
        }

        if (QueryParams && QueryParams.switch) {
            url.searchParams.set('switch', QueryParams.switch);
        }

        if (QueryParams && QueryParams.type) {
            url.searchParams.set('type', QueryParams.type);
        }

        const response = await fetch(url, {
            method: 'PUT',
            body: JSON.stringify(requestBody),
            headers: {
                Authorization: headers.authToken,
                identityToken: headers.identityToken,
                'Content-Type': 'application/json',
            },
        });

        const parsedResponse: IAcquireLockResponse = await response.json();
        return parsedResponse;
    };

    const unLock = async (lockId) => {
        const queryParams: IDeleteLockRequestParams = {
            apiKey: user.getAPIKey(),
            accountId: user.getAccountId(),
            lockId,
        };
        const headers: IAcquireLockRequestHeaders = {
            authToken: user.getUserToken(),
            identityToken: user.getIdentityToken(),
        };
        let endpoint: string;
        try {
            endpoint = apiPathManager.getPath(API_IDS.STREAM_HARNESS_UNLOCK_J21);
        } catch {
            endpoint = apiPathManager.getPath(API_IDS.STREAM_HARNESS_UNLOCK);
        }
        const url = new URL(endpoint.replace('{apiKey}', queryParams.apiKey));
        url.searchParams.set('accountId', queryParams.accountId);
        url.searchParams.set('lockId', lockId);

        const response = await fetch(url, {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
                Authorization: headers.authToken,
                identityToken: headers.identityToken,
            },
        });

        const parsedResponse: IAcquireLockResponse = await response.json();
        return parsedResponse;
    };

    return {
        acquireLock,
        refreshLock,
        unLock,
    };
};
